import { isObject } from 'lodash';

import {
  appendLocaleToRelativeUrl,
  getRelativeUrlWithoutProtocolAndDomain,
} from '../../../common/utilities/internal';
import { isLinkField } from '../is-link-field';

/**
 * Removes domain from internal and external links and makes them relative. Adds language prefix.
 *
 * @param data - The data to traverse and modify.
 * @param nonDefaultLocale - The current Sitecore non-default locale.
 * @returns The modified data.
 */
export const removeDomainAndAddLanguagePrefixesInLinks = <T extends Record<string, any>>(
  data: T,
  nonDefaultLocale?: string
): T => {
  if (!isObject(data)) return data;

  if (Array.isArray(data)) {
    return data.map((item) =>
      removeDomainAndAddLanguagePrefixesInLinks(item, nonDefaultLocale)
    ) as unknown as T;
  }

  const modifiedData: T = {} as T;
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if (isLinkField(data[key])) {
        if (data[key].value.linktype === 'internal') {
          // For internal links, remove protocol, domains, prepend the locale if needed
          modifiedData[key] = {
            ...data[key],
            value: {
              ...data[key].value,
              href: appendLocaleToRelativeUrl(
                getRelativeUrlWithoutProtocolAndDomain(data[key].value.href),
                nonDefaultLocale
              ),
            },
          };
        } else if (data[key].value.linktype === 'external') {
          // For external links, check if it's a relative URL and prepend the locale if needed
          modifiedData[key] = {
            ...data[key],
            value: {
              ...data[key].value,
              href: appendLocaleToRelativeUrl(data[key].value.href, nonDefaultLocale),
            },
          };
        } else {
          modifiedData[key] = data[key]; // For other link types, just copy them as it is
        }
      } else if (isObject(data[key]) || Array.isArray(data[key])) {
        modifiedData[key] = removeDomainAndAddLanguagePrefixesInLinks(data[key], nonDefaultLocale);
      } else {
        modifiedData[key] = data[key]; // For other non-object fields, just copy them as it is
      }
    }
  }

  return modifiedData;
};
